/**
 * Parking - port from `1116/omega-club/dashboard/src/components/Parking.jsx`.
 * Reads from `useTenantData(slug).parking` (DB-backed via
 * parking_revenue.scenario_data) instead of the omega-club hardcoded
 * `parkingData` import.
 *
 * Per CHAP-88 (Phase 1C #54). Optional component: renders only when the
 * tenant has feature_flags.parking_enabled=true AND a parking_revenue row;
 * the tenant-config route sets `parking` to null otherwise and this page
 * shows a "not enabled" state.
 *
 * Parity notes vs omega-club:
 * - `projections` (25-year) are computed here from `proposed` (3% annual
 *   growth, 26 years 2026-2051) rather than stored, so they never drift
 *   from the stored permit/game-day figures.
 * - `proposed.totalRevenue` falls back to annualRevenue + gameDayRevenue
 *   when not stored.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useTenantData } from '../hooks/useTenantData.js';
import { TenantLoading, TenantError } from '../components/TenantDataStates.js';
import { formatCurrencyFull, formatNumber } from '../lib/formatters.js';
import { Tooltip } from '../components/Tooltip.js';
import type { ParkingData } from '../../shared/types.js';

const GRAPH_BLUE = '#3B82F6';
const GRAPH_AMBER = '#F59E0B';
const GRAPH_GREEN = '#22C55E';

interface Projection {
  year: number;
  permits: number;
  gameDay: number;
  total: number;
}

export default function Parking() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading, error, refetch } = useTenantData(slug ?? '');

  const parking = data?.parking ?? null;

  const projections = useMemo<Projection[]>(() => {
    if (!parking) return [];
    const { proposed } = parking;
    const total = proposed.totalRevenue ?? proposed.annualRevenue + proposed.gameDayRevenue;
    return Array.from({ length: 26 }, (_, i) => ({
      year: 2026 + i,
      permits: Math.round(proposed.annualRevenue * Math.pow(1.03, i)),
      gameDay: Math.round(proposed.gameDayRevenue * Math.pow(1.03, i)),
      total: Math.round(total * Math.pow(1.03, i)),
    }));
  }, [parking]);

  if (isLoading) return <TenantLoading />;
  if (error) return <TenantError message={String(error)} onRetry={() => refetch()} />;
  if (!data) return <PageState label="No data" />;
  if (!parking) {
    return (
      <PageState label="Parking analysis is not enabled for this chapter." detail="Enable the parking_enabled feature flag and seed a parking_revenue row to show it." />
    );
  }

  const { current, proposed, schemes, gameDayDetails } = parking;
  const proposedTotal = proposed.totalRevenue ?? proposed.annualRevenue + proposed.gameDayRevenue;
  const currentTotal = current.annualRevenue + current.gameDayRevenue;
  const multiplier = current.annualRevenue > 0 ? Math.round(proposedTotal / current.annualRevenue) : 0;
  const permitPct = proposedTotal > 0 ? Math.round((proposed.annualRevenue / proposedTotal) * 100) : 0;
  const gameDayPct = 100 - permitPct;

  return (
    <div className="space-y-4 sm:space-y-5 min-w-0">
      {/* Header with delta callout */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-navy text-xl sm:text-2xl font-bold">Parking Analysis</h2>
            <p className="text-neutral text-sm sm:text-base mt-1">
              Revenue streams from {formatNumber(proposed.spaces)} parking spaces
              {data.tenant.address ? ` at ${data.tenant.address}` : ''}
            </p>
          </div>
          {multiplier > 0 && (
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-xs uppercase tracking-wide text-neutral font-bold">Revenue lift</span>
              <span className="text-positive text-2xl sm:text-3xl font-bold">{multiplier}×</span>
              <span className="text-xs text-neutral">{formatCurrencyFull(current.annualRevenue)} → {formatCurrencyFull(proposedTotal)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Section 1: Current vs Proposed */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 min-w-0">
        {/* Current */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4 min-w-0">
            <span className="text-xs bg-gray-200 text-gray-600 px-2.5 py-0.5 rounded-full font-medium uppercase tracking-wide">Current</span>
            <span className="text-xs text-gray-400">Baseline</span>
          </div>
          <div className="space-y-3 min-w-0">
            <ParkingStat label="Spaces" value={formatNumber(current.spaces)} hero />
            <ParkingStat label="Revenue per Space / Year" value={formatCurrencyFull(current.revenuePerSpace)} />
            <ParkingStat label="Permit Revenue" value={formatCurrencyFull(current.annualRevenue)} />
            <ParkingStat label="Game Day Revenue" value={formatCurrencyFull(current.gameDayRevenue)} muted />
            <div className="pt-2 border-t border-gray-100">
              <ParkingStat label="Total Annual Revenue" value={formatCurrencyFull(currentTotal)} hero />
            </div>
          </div>
        </div>

        {/* Proposed */}
        <div className="bg-white rounded-xl shadow-sm border-2 border-orange p-4 sm:p-6 relative min-w-0">
          <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4 min-w-0">
            <span className="text-xs bg-orange text-white px-2.5 py-0.5 rounded-full font-medium uppercase tracking-wide">Proposed</span>
            {multiplier > 0 && (
              <Tooltip content={`${multiplier}× revenue increase from ${formatCurrencyFull(current.annualRevenue)} to ${formatCurrencyFull(proposedTotal)} through premium pricing and game day events.`}>
                <span className="text-xs bg-positive/10 text-positive px-2 py-0.5 rounded-full font-bold cursor-help">+{multiplier}× revenue</span>
              </Tooltip>
            )}
          </div>
          <div className="space-y-3 min-w-0">
            <ParkingStat label="Spaces" value={formatNumber(proposed.spaces)} hero />
            <ParkingStat label="Revenue per Space / Year" value={formatCurrencyFull(proposed.revenuePerSpace)} accent />
            <ParkingStat label="Permit Revenue" value={formatCurrencyFull(proposed.annualRevenue)} />
            <ParkingStat label="Game Day Revenue" value={formatCurrencyFull(proposed.gameDayRevenue)} accent />
            <div className="pt-2 border-t border-orange/30">
              <ParkingStat label="Total Annual Revenue" value={formatCurrencyFull(proposedTotal)} hero accent />
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Revenue Breakdown */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-navy font-semibold text-sm sm:text-base mb-3 sm:mb-4">Revenue Breakdown</h3>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 min-w-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5 min-w-0">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Member Permits</span>
              <span className="text-xs sm:text-sm font-bold text-navy">{formatCurrencyFull(proposed.annualRevenue)} ({permitPct}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4">
              <div className="h-full rounded-full" style={{ width: `${permitPct}%`, backgroundColor: GRAPH_BLUE }} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1.5 min-w-0">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Game Day Events</span>
              <span className="text-xs sm:text-sm font-bold text-navy">{formatCurrencyFull(proposed.gameDayRevenue)} ({gameDayPct}%)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 sm:h-4">
              <div className="h-full rounded-full" style={{ width: `${gameDayPct}%`, backgroundColor: GRAPH_AMBER }} />
            </div>
          </div>
        </div>
      </div>

      {/* Section 3: Game Day Opportunity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3 sm:mb-4 min-w-0">
          <h3 className="text-navy font-semibold text-sm sm:text-base">Game Day Opportunity</h3>
          <Tooltip content="This revenue stream does not exist today. The proposed layout and proximity to campus make game day parking a significant new income source.">
            <span className="text-xs bg-orange/10 text-orange px-2 py-0.5 rounded-full font-medium cursor-help">New revenue</span>
          </Tooltip>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 min-w-0">
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 min-w-0">
            <p className="text-neutral text-xs uppercase tracking-wider">Football</p>
            <p className="text-navy text-lg sm:text-xl font-bold">{gameDayDetails.footballGames} games</p>
            <p className="text-neutral text-xs mt-1">~{formatCurrencyFull(gameDayDetails.revenuePerGame)} per game</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 min-w-0">
            <p className="text-neutral text-xs uppercase tracking-wider">Other Events</p>
            <p className="text-navy text-lg sm:text-xl font-bold">{gameDayDetails.otherEvents} events</p>
            <p className="text-neutral text-xs mt-1">~{formatCurrencyFull(gameDayDetails.revenuePerOtherEvent)} per event</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 sm:p-4 min-w-0">
            <p className="text-neutral text-xs uppercase tracking-wider">Total Game Day</p>
            <p className="text-orange text-lg sm:text-xl font-bold">~{formatCurrencyFull(gameDayDetails.totalGameDay)}/yr</p>
            <p className="text-neutral text-xs mt-1">Football + other events</p>
          </div>
        </div>
        {gameDayDetails.note && <p className="text-neutral text-xs sm:text-sm mt-3 sm:mt-4 italic">{gameDayDetails.note}</p>}
      </div>

      {/* Section 4: 25-Year Revenue Projection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <h3 className="text-navy font-semibold text-sm sm:text-base mb-1">25-Year Revenue Projection</h3>
        <p className="text-neutral text-xs sm:text-sm mb-3 sm:mb-4">3% annual growth from 2026 to 2051</p>
        <div className="w-full min-w-0" style={{ height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={projections} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="year" tick={{ fontSize: 11 }} tickFormatter={(v) => (v % 5 === 1 ? v : '')} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} stroke="#94a3b8" width={55} />
              <RechartsTooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Line type="monotone" dataKey="permits" name="Member Permits" stroke={GRAPH_BLUE} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="gameDay" name="Game Day" stroke={GRAPH_AMBER} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              <Line type="monotone" dataKey="total" name="Total" stroke={GRAPH_GREEN} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Section 5: Scheme Comparison */}
      {schemes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <h3 className="text-navy font-semibold text-sm sm:text-base mb-3 sm:mb-4">Scheme Comparison</h3>
          <div className="overflow-x-auto -mx-4 sm:mx-0 min-w-0">
            <table className="w-full text-xs sm:text-sm min-w-[480px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 sm:px-4 text-neutral font-medium uppercase tracking-wider">Scheme</th>
                  <th className="text-right py-2 px-3 sm:px-4 text-neutral font-medium uppercase tracking-wider">Spaces</th>
                  <th className="text-right py-2 px-3 sm:px-4 text-neutral font-medium uppercase tracking-wider">$/Space/Yr</th>
                  <th className="text-right py-2 px-3 sm:px-4 text-neutral font-medium uppercase tracking-wider">Annual Revenue</th>
                  <th className="text-right py-2 px-3 sm:px-4 text-neutral font-medium uppercase tracking-wider">Deck Cost</th>
                </tr>
              </thead>
              <tbody>
                {schemes.map((scheme) => {
                  const isProposed = /proposed \(final\)/i.test(scheme.name);
                  return (
                    <tr key={scheme.name} className={`border-b border-gray-100 ${isProposed ? 'bg-orange/5 font-semibold' : ''}`}>
                      <td className={`py-2.5 px-3 sm:px-4 ${isProposed ? 'text-orange' : 'text-navy'}`}>
                        {scheme.name}
                        {scheme.note && <span className="block text-xs text-neutral font-normal">{scheme.note}</span>}
                      </td>
                      <td className="text-right py-2.5 px-3 sm:px-4 text-navy">{formatNumber(scheme.spaces)}</td>
                      <td className="text-right py-2.5 px-3 sm:px-4 text-navy">{formatCurrencyFull(scheme.revenuePerSpace)}</td>
                      <td className={`text-right py-2.5 px-3 sm:px-4 ${isProposed ? 'text-orange' : 'text-navy'}`}>{formatCurrencyFull(scheme.annual)}</td>
                      <td className="text-right py-2.5 px-3 sm:px-4 text-navy">{scheme.deckCost > 0 ? formatCurrencyFull(scheme.deckCost) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ParkingStat({ label, value, hero, muted, accent }: { label: string; value: string; hero?: boolean; muted?: boolean; accent?: boolean }) {
  const valueClass = hero
    ? `text-xl sm:text-2xl font-bold ${accent ? 'text-orange' : 'text-navy'}`
    : muted
      ? 'text-lg font-bold text-neutral'
      : accent
        ? 'text-lg font-bold text-orange'
        : 'text-lg font-bold text-navy';
  return (
    <div className="min-w-0">
      <p className="text-neutral text-xs uppercase tracking-wider">{label}</p>
      <p className={valueClass}>{value}</p>
    </div>
  );
}

interface ChartTooltipProps {
  active?: boolean;
  label?: string | number;
  payload?: Array<{ name: string; value: number; color: string }>;
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-xs min-w-0">
      <p className="font-semibold text-navy mb-1">{label}</p>
      {payload.map((entry) => (
        <p key={entry.name} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-navy">{formatCurrencyFull(entry.value)}</span>
        </p>
      ))}
    </div>
  );
}

function PageState({ label, detail, tone }: { label: string; detail?: string; tone?: 'error' }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center min-w-0">
      <p className={`text-sm font-medium ${tone === 'error' ? 'text-negative' : 'text-navy'}`}>{label}</p>
      {detail && <p className="text-neutral text-xs mt-1">{detail}</p>}
    </div>
  );
}

export type { ParkingData };
